import { useCallback } from 'react';
import {
  useAgentsQuery,
  useCreateSessionMutation,
  useSendMessageMutation,
} from '../api';
import { AgentForgeApiError } from '../api/http';
import { useAgentForgeStore } from '../stores';
import type { ChatSubmit } from './useChatSubmit.types';

const isStaleSessionError = (err: unknown): boolean =>
  err instanceof AgentForgeApiError && err.status === 404;

const DEFAULT_AGENT_SLUG = 'pipeline-manager';

// Composes "resolve-or-create session → append user msg → mark streaming".
// Each consumer wires the returned `send` into Composer.onSubmit. The real
// path POSTs to `/sessions/{id}/messages` and flips the tab status to
// `streaming`. The reply lands via V1's WebSocket (`useAgentForgeStream`)
// and the canonical record is fetched by `useAgentForgeMessages` after
// `stream_done`.
//
// `send` returns the sessionId so callers can navigate to a per-session
// route after a fresh session is created. Returns `null` on empty input
// or session-resolution failure.
//
// `activeSessionId` is read via `useAgentForgeStore.getState()` inside the
// callback (not via a closing selector) so callers that openTab a
// pre-warmed session synchronously *before* calling `send` see the new
// active session in the same tick — without the callback having to
// recreate first.
export const useChatSubmit = (): ChatSubmit => {
  const openTab = useAgentForgeStore((s) => s.openTab);
  const closeTab = useAgentForgeStore((s) => s.closeTab);
  const setMode = useAgentForgeStore((s) => s.setMode);
  const appendUserMessage = useAgentForgeStore((s) => s.appendUserMessage);
  const setTabStatus = useAgentForgeStore((s) => s.setTabStatus);

  const agentsQuery = useAgentsQuery();
  const createSessionMutation = useCreateSessionMutation();
  const sendMessage = useSendMessageMutation();

  const send = useCallback(
    async (text: string): Promise<string | null> => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return null;

      const activeSessionId = useAgentForgeStore.getState().activeSessionId;

      const startFreshSession = async (): Promise<string | null> => {
        const agents = agentsQuery.data ?? [];
        const agent =
          agents.find((a) => a.slug === DEFAULT_AGENT_SLUG) ?? agents[0];
        if (!agent) return null;
        const session = await createSessionMutation.mutateAsync({
          agent_id: agent.id,
          title: trimmed,
        });
        openTab({
          sessionId: session.id,
          title: session.title || trimmed,
          status: 'idle',
          createdAt: session.created_at,
        });
        setMode('floating');
        return session.id;
      };

      const stampOptimistic = (id: string) => {
        appendUserMessage(id, trimmed);
        setTabStatus(id, 'streaming');
      };

      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      let sessionId = activeSessionId;
      if (!sessionId) {
        try {
          sessionId = await startFreshSession();
        } catch {
          return null;
        }
        if (!sessionId) return null;
      }

      stampOptimistic(sessionId);

      try {
        await sendMessage.mutateAsync({
          sessionId,
          message: trimmed,
          timezone: tz,
        });
        return sessionId;
      } catch (err) {
        if (!isStaleSessionError(err)) {
          setTabStatus(sessionId, 'error');
          return sessionId;
        }
      }

      // Stale persisted session — backend doesn't know it. Drop the dead tab
      // (also clears its optimistic messages), create a fresh one, retry once.
      closeTab(sessionId);
      let recoveredId: string | null = null;
      try {
        recoveredId = await startFreshSession();
      } catch {
        return null;
      }
      if (!recoveredId) return null;
      stampOptimistic(recoveredId);
      try {
        await sendMessage.mutateAsync({
          sessionId: recoveredId,
          message: trimmed,
          timezone: tz,
        });
      } catch {
        setTabStatus(recoveredId, 'error');
      }
      return recoveredId;
    },
    [
      openTab,
      closeTab,
      setMode,
      appendUserMessage,
      setTabStatus,
      agentsQuery.data,
      createSessionMutation,
      sendMessage,
    ]
  );

  return {
    send,
    isSending: createSessionMutation.isPending || sendMessage.isPending,
  };
};
